// What Classify and Import plug into the shared attachments hook (hooks/useWorkflowAttachments): their file
// rules, their upload endpoint, and the fields a turn carries to name the files it uses.
import type { WorkflowAttachmentAdapter } from 'hooks/useWorkflowAttachments';
import {
  ACCEPTED_ATTACHMENT_TYPES,
  AttachmentLimits,
  bytesSentToModel,
  checkAttachment,
  DEFAULT_ATTACHMENT_LIMITS,
  newClassifyConversationId,
  tooBigTogetherMessage,
  tooManyFilesMessage,
} from './classifyAttachments';
import {
  ClassifyAttachmentRef,
  classifyAuthHeaders,
  uploadClassifyAttachment,
} from './classifyUpload';

export const makeClassifyAttachmentAdapter = (
  apiUrl: string,
  limits: AttachmentLimits = DEFAULT_ATTACHMENT_LIMITS,
): WorkflowAttachmentAdapter<ClassifyAttachmentRef> => ({
  accept: ACCEPTED_ATTACHMENT_TYPES,
  check: (file) => checkAttachment(file, limits),
  maxFiles: limits.maxFilesPerTurn,
  tooManyFilesMessage: tooManyFilesMessage,
  maxTotalBytes: limits.maxMessageBytes,
  bytesOf: bytesSentToModel,
  tooBigTogetherMessage: (details) =>
    tooBigTogetherMessage({
      limits: limits,
      totalBytes: details.totalBytes,
      added: details.added,
      skipped: details.skipped,
    }),
  upload: (file, context) =>
    uploadClassifyAttachment(
      file,
      context.conversationId,
      context.groupId,
      apiUrl,
    ),
  filenameOf: (ref) => ref.filename,
  newConversationId: newClassifyConversationId,
});

/**
 * The extra fields a Classify / Import turn sends when it carries files. Nothing for a turn without any,
 * so a plain turn is byte-for-byte what it was before.
 */
export const attachmentTurnFields = (
  attachments: ClassifyAttachmentRef[],
  groupId: string | undefined,
): { group_id?: string; attachments?: ClassifyAttachmentRef[] } =>
  attachments.length > 0
    ? { group_id: groupId, attachments: attachments }
    : {};

/**
 * The request config for a turn that carries files: it must prove who is asking, because the server
 * checks team membership before it reads a file. A plain turn sends no token, exactly as before.
 */
export const attachmentTurnConfig = async (
  attachments: ClassifyAttachmentRef[],
): Promise<{ headers: Record<string, string> } | undefined> =>
  attachments.length > 0
    ? { headers: await classifyAuthHeaders() }
    : undefined;
